/**
 * useGiveawaysState - Custom hook for Giveaways module state management
 * Extracted from Admin.jsx to reduce state complexity
 * 
 * No changes to behavior, API endpoints, or request payloads.
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../utils/apiUrl';

const getAuthHeaders = () => {
  const token = sessionStorage.getItem('golfnuts_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const initialGiveawayForm = {
  title: '',
  heroImage: '',
  description: '',
  rules: '',
  primaryCtaLabel: 'Enter Now',
  primaryCtaUrl: '',
  secondaryCtaLabel: '',
  secondaryCtaUrl: '',
  endDate: '',
  eligibilityNotes: '',
  isActive: false
};

// Helper function to retry requests with exponential backoff
const retryRequest = async (requestFn, maxRetries = 2) => {
  let lastError;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (err) {
      lastError = err;
      const isRetryable = !err.response || err.response.status === 503 || err.response.status === 504;
      if (!isRetryable || attempt === maxRetries) throw err;
      await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
    }
  }
  throw lastError;
};

export const useGiveawaysState = ({ setError, setSuccess }) => {
  // Core giveaways state
  const [giveaways, setGiveaways] = useState([]);
  const [loadingGiveaways, setLoadingGiveaways] = useState(false);
  const [selectedGiveaway, setSelectedGiveaway] = useState(null);
  const [showGiveawayEditor, setShowGiveawayEditor] = useState(false);
  const [giveawayForm, setGiveawayForm] = useState({ ...initialGiveawayForm });

  // Reset form to initial state
  const resetGiveawayForm = useCallback(() => {
    setGiveawayForm({ ...initialGiveawayForm });
  }, []);

  // Fetch all giveaways
  const fetchGiveaways = useCallback(async () => {
    setLoadingGiveaways(true);
    try {
      const response = await axios.get(`${API_URL}/api/cms/giveaways`, {
        headers: getAuthHeaders()
      });
      setGiveaways(response.data || []);
    } catch (err) {
      console.error('Error fetching giveaways:', err);
      setError('Failed to load giveaways');
    } finally {
      setLoadingGiveaways(false);
    }
  }, [setError]);

  // Create new giveaway
  const handleCreateGiveaway = useCallback(async () => {
    try {
      const response = await retryRequest(() =>
        axios.post(`${API_URL}/api/cms/giveaways`, giveawayForm, {
          headers: getAuthHeaders(),
          timeout: 60000 // 60 second timeout for large image uploads
        })
      );
      setGiveaways(prev => [response.data, ...prev]);
      setSuccess('Giveaway created successfully');
      setShowGiveawayEditor(false);
      resetGiveawayForm();
    } catch (err) {
      let errorMsg = 'Failed to create giveaway: ';
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMsg += 'Request timed out. Please try again with a smaller image or check your connection.';
      } else if (err.response?.status === 503) {
        errorMsg += 'Service temporarily unavailable. Please try again in a few moments.';
      } else if (err.response?.status === 413) {
        errorMsg += 'Image is too large. Please use a smaller image.';
      } else {
        errorMsg += err.response?.data?.detail || err.message;
      }
      setError(errorMsg);
    }
  }, [giveawayForm, resetGiveawayForm, setError, setSuccess]);

  // Update existing giveaway
  const handleUpdateGiveaway = useCallback(async () => {
    if (!selectedGiveaway) return;
    
    try {
      const response = await retryRequest(() => 
        axios.put(
          `${API_URL}/api/cms/giveaways/${selectedGiveaway.id}`,
          giveawayForm,
          { 
            headers: getAuthHeaders(),
            timeout: 60000
          }
        )
      );
      setGiveaways(prev => prev.map(g => g.id === selectedGiveaway.id ? response.data : g));
      setSuccess('Giveaway updated successfully');
      setShowGiveawayEditor(false);
      setSelectedGiveaway(null);
      resetGiveawayForm();
    } catch (err) {
      let errorMsg = 'Failed to update giveaway: ';
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMsg += 'Request timed out. Please try again with a smaller image or check your connection.';
      } else if (err.response?.status === 503) {
        errorMsg += 'Service temporarily unavailable. Please try again in a few moments.';
      } else if (err.response?.status === 413) {
        errorMsg += 'Image is too large. Please use a smaller image.';
      } else {
        errorMsg += err.response?.data?.detail || err.message;
      }
      setError(errorMsg);
    }
  }, [selectedGiveaway, giveawayForm, resetGiveawayForm, setError, setSuccess]);

  // Activate giveaway (make visible on homepage)
  const handleActivateGiveaway = useCallback(async (giveawayId) => {
    try {
      await axios.put(
        `${API_URL}/api/cms/giveaways/${giveawayId}/activate`,
        {},
        { headers: getAuthHeaders() }
      );
      fetchGiveaways();
      setSuccess('Giveaway activated! It will now appear on the homepage.');
    } catch (err) {
      setError('Failed to activate giveaway: ' + (err.response?.data?.detail || err.message));
    }
  }, [fetchGiveaways, setError, setSuccess]);

  // Deactivate giveaway (hide from homepage)
  const handleDeactivateGiveaway = useCallback(async (giveawayId) => {
    try {
      await axios.put(
        `${API_URL}/api/cms/giveaways/${giveawayId}/deactivate`,
        {},
        { headers: getAuthHeaders() }
      );
      fetchGiveaways();
      setSuccess('Giveaway deactivated. It will no longer appear on the homepage.');
    } catch (err) {
      setError('Failed to deactivate giveaway: ' + (err.response?.data?.detail || err.message));
    }
  }, [fetchGiveaways, setError, setSuccess]);

  // Delete giveaway
  const handleDeleteGiveaway = useCallback(async (giveawayId) => {
    const giveaway = giveaways.find(g => g.id === giveawayId);
    if (!giveaway) return;
    
    if (giveaway.isActive) {
      if (!window.confirm(`"${giveaway.title}" is currently ACTIVE on the homepage. Are you sure you want to delete it?`)) {
        return;
      }
    } else if (!window.confirm(`Are you sure you want to delete "${giveaway.title}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/cms/giveaways/${giveawayId}`, {
        headers: getAuthHeaders()
      });
      setGiveaways(prev => prev.filter(g => g.id !== giveawayId));
      setSuccess('Giveaway deleted successfully');
    } catch (err) {
      setError('Failed to delete giveaway: ' + (err.response?.data?.detail || err.message));
    }
  }, [giveaways, setError, setSuccess]);

  // Open giveaway editor (for create or edit)
  const openGiveawayEditor = useCallback((giveaway = null) => {
    if (giveaway) {
      setSelectedGiveaway(giveaway);
      setGiveawayForm({
        title: giveaway.title || '',
        heroImage: giveaway.heroImage || '',
        description: giveaway.description || '',
        rules: giveaway.rules || '',
        primaryCtaLabel: giveaway.primaryCtaLabel || 'Enter Now',
        primaryCtaUrl: giveaway.primaryCtaUrl || '',
        secondaryCtaLabel: giveaway.secondaryCtaLabel || '',
        secondaryCtaUrl: giveaway.secondaryCtaUrl || '',
        endDate: giveaway.endDate ? giveaway.endDate.split('T')[0] : '',
        eligibilityNotes: giveaway.eligibilityNotes || '',
        isActive: giveaway.isActive || false
      });
    } else {
      setSelectedGiveaway(null);
      resetGiveawayForm();
    }
    setShowGiveawayEditor(true);
  }, [resetGiveawayForm]);

  // Handle form field changes
  const handleGiveawayFieldChange = useCallback((field, value) => {
    setGiveawayForm(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  return {
    // State
    giveaways,
    loadingGiveaways,
    selectedGiveaway,
    showGiveawayEditor,
    giveawayForm,

    // Setters
    setGiveaways,
    setSelectedGiveaway,
    setShowGiveawayEditor,
    setGiveawayForm,

    // Actions
    fetchGiveaways,
    handleCreateGiveaway,
    handleUpdateGiveaway,
    handleActivateGiveaway,
    handleDeactivateGiveaway,
    handleDeleteGiveaway,
    openGiveawayEditor,
    handleGiveawayFieldChange,
    resetGiveawayForm,
  };
};

export default useGiveawaysState;
